import { useState } from "react";
import {
  SquaresFour,
  ChartBar,
  Megaphone,
  ShareNetwork,
  MagnifyingGlassPlus,
  ShoppingCart,
  DotsThreeCircle,
} from "@phosphor-icons/react";
import type { Icon } from "@phosphor-icons/react";
import { logoUrl, TOTAL_INTEGRATIONS } from "../../lib/integrations";
import { SectionArrow } from "./SectionArrow";

type CategoryKey =
  | "all"
  | "analytics"
  | "advertising"
  | "social"
  | "seo"
  | "commerce"
  | "other";

interface Integration {
  name: string;
  domain: string;
  category: Exclude<CategoryKey, "all">;
}

const integrations: Integration[] = [
  // Analytics
  { name: "Google Analytics 4", domain: "analytics.google.com", category: "analytics" },
  { name: "Search Console", domain: "search.google.com", category: "analytics" },
  { name: "YouTube Analytics", domain: "youtube.com", category: "analytics" },
  { name: "Business Profile", domain: "business.google.com", category: "analytics" },
  { name: "PageSpeed Insights", domain: "pagespeed.web.dev", category: "analytics" },

  // Advertising
  { name: "Google Ads", domain: "ads.google.com", category: "advertising" },
  { name: "Facebook Ads", domain: "facebook.com", category: "advertising" },
  { name: "Instagram Ads", domain: "instagram.com", category: "advertising" },
  { name: "LinkedIn Ads", domain: "linkedin.com", category: "advertising" },
  { name: "Microsoft Ads", domain: "ads.microsoft.com", category: "advertising" },
  { name: "Pinterest Ads", domain: "pinterest.com", category: "advertising" },
  { name: "Snapchat Ads", domain: "snapchat.com", category: "advertising" },
  { name: "Reddit Ads", domain: "reddit.com", category: "advertising" },
  { name: "Amazon Ads", domain: "advertising.amazon.com", category: "advertising" },

  // Social
  { name: "Facebook Pages", domain: "facebook.com", category: "social" },
  { name: "Instagram Business", domain: "instagram.com", category: "social" },
  { name: "LinkedIn Pages", domain: "linkedin.com", category: "social" },
  { name: "TikTok Business", domain: "tiktok.com", category: "social" },

  // SEO
  { name: "Ahrefs", domain: "ahrefs.com", category: "seo" },
  { name: "Semrush", domain: "semrush.com", category: "seo" },
  { name: "Moz", domain: "moz.com", category: "seo" },

  // Commerce & Email
  { name: "Shopify", domain: "shopify.com", category: "commerce" },
  { name: "Mailchimp", domain: "mailchimp.com", category: "commerce" },
  { name: "Klaviyo", domain: "klaviyo.com", category: "commerce" },

  // Other (CRM, Reviews, Call Tracking)
  { name: "HubSpot", domain: "hubspot.com", category: "other" },
  { name: "Google Reviews", domain: "google.com", category: "other" },
  { name: "Trustpilot", domain: "trustpilot.com", category: "other" },
  { name: "Yelp", domain: "yelp.com", category: "other" },
  { name: "CallRail", domain: "callrail.com", category: "other" },
  { name: "CallTrackingMetrics", domain: "calltrackingmetrics.com", category: "other" },
];

const filters: { key: CategoryKey; label: string; icon: Icon }[] = [
  { key: "all", label: "All", icon: SquaresFour },
  { key: "analytics", label: "Analytics", icon: ChartBar },
  { key: "advertising", label: "Advertising", icon: Megaphone },
  { key: "social", label: "Social", icon: ShareNetwork },
  { key: "seo", label: "SEO", icon: MagnifyingGlassPlus },
  { key: "commerce", label: "Commerce", icon: ShoppingCart },
  { key: "other", label: "Other", icon: DotsThreeCircle },
];

export function IntegrationsSection() {
  const [activeFilter, setActiveFilter] = useState<CategoryKey>("all");

  const visible =
    activeFilter === "all"
      ? integrations
      : integrations.filter((i) => i.category === activeFilter);

  return (
    <div id="integrations" className="pt-8 pb-12 sm:pt-12 sm:pb-14 scroll-mt-20">
      {/* px-6 on mobile keeps the integrations grid card off the screen edges; desktop sm:px-8. */}
      <div className="max-w-[1200px] mx-auto px-6 sm:px-8">
        <div className="text-center mb-10">
          <SectionArrow />
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground">
            Connect all your data sources
          </h2>
          <p className="mt-4 text-lg text-foreground/85 max-w-[560px] mx-auto">
            {TOTAL_INTEGRATIONS} integrations across analytics, advertising, social, SEO, reviews,
            and more.
          </p>
        </div>

        {/* Filter tabs — pills */}
        <div className="flex flex-wrap justify-center gap-2 mb-8 sm:mb-16">
          {filters.map((filter) => {
            const isActive = activeFilter === filter.key;
            const Icon = filter.icon;
            return (
              <button
                key={filter.key}
                type="button"
                onClick={() => setActiveFilter(filter.key)}
                className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full transition-colors cursor-pointer ${
                  isActive
                    ? "bg-muted/50 text-foreground shadow-xs"
                    : "bg-white border border-[hsl(213_27%_86%)] text-foreground hover:bg-background hover:shadow-xs"
                }`}
                style={isActive ? { border: "0.5px solid hsl(var(--foreground))" } : undefined}
              >
                <Icon size={18} className="text-foreground" />
                {filter.label}
              </button>
            );
          })}
        </div>

        {/* Integration grid */}
        <div
          className="bg-white shadow-xs rounded-[32px] grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 overflow-hidden"
          style={{ border: "0.5px solid var(--hairline)" }}
        >
          {visible.map((integration, index) => {
            const totalCols = 6;
            const col = index % totalCols;
            const isLastCol = col === totalCols - 1;
            const row = Math.floor(index / totalCols);
            const totalRows = Math.ceil(visible.length / totalCols);
            const isLastRow = row === totalRows - 1;
            return (
              <div
                key={integration.name}
                className={`flex flex-col items-center justify-center gap-3 px-4 py-6 ${!isLastCol ? "lg:border-r-[0.5px] lg:border-[var(--hairline)]" : ""} ${!isLastRow ? "lg:border-b-[0.5px] lg:border-[var(--hairline)]" : ""}`}
              >
                <img
                  src={logoUrl(integration.domain)}
                  alt=""
                  width={24}
                  height={24}
                  className="shrink-0 rounded-sm"
                  loading="lazy"
                />
                <span className="text-[14px] font-medium text-foreground/85 text-center leading-tight">
                  {integration.name}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
